import * as L from 'leaflet';
import proj4 from 'proj4';
import { StorageService } from '../../services/storage/storage.service';
import type { MBTilesService } from '../services/mbtiles.service';

// Projektionsdefinitionen für CRS-Transformation
proj4.defs('EPSG:25833', '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs');
proj4.defs('EPSG:25832', '+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs');
proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs +type=crs');

/**
 * Optionen für den OfflineGridLayer
 */
export interface OfflineGridLayerOptions extends L.GridLayerOptions {
	project: string;
	name: string;
	maxNativeZoom?: number;
	globalStore?: string;    // Globaler IndexedDB-Store für Basiskarten-Tiles (z.B. '_global_osm')
	globalMaxZoom?: number;  // Maximale Zoomstufe im globalen Store (z.B. 12)
	onlineUrl?: string;      // URL-Template für Online-Tiles (z.B. 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png')
	onlineSubdomains?: string[];  // Subdomains für Online-Tiles (z.B. ['a', 'b', 'c'])
	lowZoomUrl?: string;     // URL-Template für niedrige Zoomstufen (z.B. offizielles OSM für z7-10)
	lowZoomMaxZoom?: number;  // Maximale Zoomstufe für lowZoomUrl (z.B. 10)
	// WMS-spezifische Optionen
	wmsUrl?: string;         // Basis-URL für WMS-Service
	wmsLayers?: string;      // WMS-Layer-Name(n) - Komma-separiert
	wmsFormat?: string;      // WMS-Format (default: 'image/png')
	wmsTransparent?: boolean; // WMS transparent (default: true)
	wmsCrs?: string;         // WMS CRS (default: EPSG:4326, z.B. EPSG:25833)
	// Authentifizierung
	authHeader?: string;     // Authorization Header (z.B. 'Basic xxx')
	// MBTiles-Service für z7-11 Basiskarten-Tiles
	mbtiles?: MBTilesService;
	mbtilesMaxZoom?: number;  // Maximale Zoomstufe in der MBTiles-Datei (z.B. 11)
	// Optionale Pixel-Transformation nach dem Zeichnen auf Canvas (z.B. DGM-Farbkalibrierung)
	pixelTransform?: (imageData: ImageData) => void;
}

/**
 * GridLayer, der Tiles aus IndexedDB (via StorageService) liest.
 * - Fallback auf Assets wenn in IndexedDB nicht gefunden
 * - Online-Fallback für höhere Zoomstufen (wenn onlineUrl gesetzt)
 * - maxNativeZoom: Bei höheren Zoomstufen wird hochskaliert
 */
export class OfflineGridLayer extends L.GridLayer {
	declare options: OfflineGridLayerOptions;
	private _maxNativeZoom?: number;
	private _globalStore?: string;
	private _globalMaxZoom?: number;
	private _onlineUrl?: string;
	private _onlineSubdomains?: string[];
	private _lowZoomUrl?: string;
	private _lowZoomMaxZoom?: number;
	// WMS-spezifische Felder
	private _wmsUrl?: string;
	private _wmsLayers?: string;
	private _wmsFormat: string;
	private _wmsTransparent: boolean;
	private _wmsCrs: string;
	// Auth
	private _authHeader?: string;
	// MBTiles
	private _mbtiles?: MBTilesService;
	private _mbtilesMaxZoom?: number;
	// Memory-Cache für schnelleren Zugriff (LRU mit max 500 Tiles)
	private static _tileCache = new Map<string, string>();
	private static _tileCacheOrder: string[] = [];

	/** Leert den gesamten Memory-Cache (z.B. bei DGM-Kalibrierungswechsel) */
	static clearTileCache(): void {
		OfflineGridLayer._tileCache.clear();
		OfflineGridLayer._tileCacheOrder = [];
	}
	private static readonly MAX_CACHE_SIZE = 500;
	// AbortController für laufende Tile-Requests - bricht veraltete Requests ab
	private _abortControllers = new Map<string, AbortController>();
	// Zähler für laufende Tile-Loads (für Ladeindikator)
	private _loadingCount = 0;

	constructor(options: OfflineGridLayerOptions, private store: StorageService) {
		// Alle Optionen an super() übergeben (GridLayerOptions inkl. bounds, minZoom, maxZoom)
		super(options as L.GridLayerOptions);
		// Custom Optionen speichern
		this._maxNativeZoom = options.maxNativeZoom;
		this._globalStore = options.globalStore;
		this._globalMaxZoom = options.globalMaxZoom;
		this._onlineUrl = options.onlineUrl;
		this._onlineSubdomains = options.onlineSubdomains || ['a', 'b', 'c'];
		this._lowZoomUrl = options.lowZoomUrl;
		this._lowZoomMaxZoom = options.lowZoomMaxZoom;
		// WMS-Optionen
		this._wmsUrl = options.wmsUrl;
		this._wmsLayers = options.wmsLayers;
		this._wmsFormat = options.wmsFormat || 'image/png';
		this._wmsTransparent = options.wmsTransparent !== false;
		this._wmsCrs = options.wmsCrs || 'EPSG:4326';
		// Auth
		this._authHeader = options.authHeader;
		// MBTiles
		this._mbtiles = options.mbtiles;
		this._mbtilesMaxZoom = options.mbtilesMaxZoom;
	}

	private _getCacheKey(key: string): string {
		return `${this.options.project}:${this.options.name}:${key}`;
	}

	private _getFromCache(key: string): string | undefined {
		const cacheKey = this._getCacheKey(key);
		const value = OfflineGridLayer._tileCache.get(cacheKey);
		if (value) {
			// LRU: Element an das Ende verschieben (= zuletzt verwendet)
			const idx = OfflineGridLayer._tileCacheOrder.indexOf(cacheKey);
			if (idx > -1) {
				OfflineGridLayer._tileCacheOrder.splice(idx, 1);
				OfflineGridLayer._tileCacheOrder.push(cacheKey);
			}
		}
		return value;
	}

	private _addToCache(key: string, dataUrl: string): void {
		const cacheKey = this._getCacheKey(key);
		// Wenn bereits im Cache, nur Position aktualisieren
		const existingIdx = OfflineGridLayer._tileCacheOrder.indexOf(cacheKey);
		if (existingIdx > -1) {
			OfflineGridLayer._tileCacheOrder.splice(existingIdx, 1);
		} else {
			// LRU: Älteste Einträge entfernen wenn voll
			while (OfflineGridLayer._tileCache.size >= OfflineGridLayer.MAX_CACHE_SIZE) {
				const oldest = OfflineGridLayer._tileCacheOrder.shift();
				if (oldest) OfflineGridLayer._tileCache.delete(oldest);
			}
		}
		OfflineGridLayer._tileCache.set(cacheKey, dataUrl);
		OfflineGridLayer._tileCacheOrder.push(cacheKey);
	}

	override createTile(coords: L.Coords, done: L.DoneCallback): HTMLCanvasElement {
		const canvas = document.createElement('canvas');
		const size = this.getTileSize();
		canvas.width = size.x;
		canvas.height = size.y;
		const ctx = canvas.getContext('2d')!;

		const maxNativeZoom = this._maxNativeZoom;
		const tileKey = this._tileKey(coords);

		// AbortController für dieses Tile erstellen (ersetzt ggf. alten)
		const oldController = this._abortControllers.get(tileKey);
		if (oldController) {
			oldController.abort();
		}
		const abortController = new AbortController();
		this._abortControllers.set(tileKey, abortController);

		// Loading-Zähler erhöhen
		this._loadingCount++;
		if (this._loadingCount === 1) {
			this.fire('loading');
		}

		// Wrapper für done-Callback: Räumt auf und feuert Events
		const wrappedDone: L.DoneCallback = (err, tile) => {
			this._abortControllers.delete(tileKey);
			this._loadingCount--;
			if (this._loadingCount === 0) {
				this.fire('load');
			}
			done(err, tile);
		};

		// Wenn maxNativeZoom gesetzt und aktuelle Zoomstufe höher: Tile von maxNativeZoom hochskalieren
		if (maxNativeZoom && coords.z > maxNativeZoom) {
			this._loadUpscaledTile(coords, maxNativeZoom, ctx, size, wrappedDone, canvas, abortController.signal);
		} else {
			// Normale Zoomstufe: Tile direkt laden
			this._loadDirectTile(coords, ctx, size, wrappedDone, canvas, abortController.signal);
		}

		return canvas;
	}

	/**
	 * Wird aufgerufen wenn ein Tile nicht mehr benötigt wird (beim Panning/Zoomen)
	 * Bricht laufende Requests ab um Bandbreite zu sparen
	 */
	protected _removeTile(key: string): void {
		// Laufenden Request abbrechen
		const controller = this._abortControllers.get(key);
		if (controller) {
			controller.abort();
			this._abortControllers.delete(key);
			this._loadingCount = Math.max(0, this._loadingCount - 1);
			if (this._loadingCount === 0) {
				this.fire('load');
			}
		}
		// @ts-ignore - protected Methode in Leaflet
		super._removeTile(key);
	}

	/**
	 * Lädt ein Tile:
	 * - WMS Online (wmsUrl gesetzt): Memory-Cache → WMS-Server
	 * - Sonst: Original-Logik (Assets, Online, IndexedDB)
	 */
	private _loadDirectTile(
		coords: L.Coords,
		ctx: CanvasRenderingContext2D,
		size: L.Point,
		done: L.DoneCallback,
		canvas: HTMLCanvasElement,
		signal?: AbortSignal
	): void {
		// WMS: Memory-Cache → IndexedDB → WMS-Server
		if (this._wmsUrl) {
			const key = this._tileKey(coords);
			const cached = this._getFromCache(key);
			if (cached) {
				this._drawImage(cached, ctx, size, done, canvas);
				return;
			}
			// IndexedDB prüfen (heruntergeladene Tiles)
			if (this.options.project) {
				this.store.getProjectValue(key, this.options.project, this.options.name).then((dataUrl: any) => {
					if (dataUrl) {
						this._addToCache(key, dataUrl);
						this._drawImage(dataUrl, ctx, size, done, canvas);
					} else if (navigator.onLine) {
						this._loadOnlineTileWithCache(coords, key, ctx, size, done, canvas, signal);
					} else {
						this._drawTransparent(ctx, size);
						done(undefined, canvas);
					}
				});
			} else if (navigator.onLine) {
				this._loadOnlineTileWithCache(coords, key, ctx, size, done, canvas, signal);
			} else {
				this._drawTransparent(ctx, size);
				done(undefined, canvas);
			}
			return;
		}

		// ===== MBTiles Basiskarte (z7-z11) =====

		if (this._mbtiles && this._mbtilesMaxZoom && coords.z >= 7 && coords.z <= this._mbtilesMaxZoom) {
			const key = this._tileKey(coords);
			const cached = this._getFromCache(key);
			if (cached) {
				this._drawImage(cached, ctx, size, done, canvas);
				return;
			}
			// Aus MBTiles lesen (synchron, da in-memory SQLite)
			if (this._mbtiles.ready()) {
				const dataUrl = this._mbtiles.getTile(coords.z, coords.x, coords.y);
				if (dataUrl) {
					this._addToCache(key, dataUrl);
					this._drawImage(dataUrl, ctx, size, done, canvas);
					return;
				}
			}
			// MBTiles noch nicht geladen oder Tile nicht vorhanden
			// Fallback: offizielles OSM (nicht Luzula-Server) für z7-z11
			if (navigator.onLine) {
				const osmUrl = `https://tile.openstreetmap.org/${coords.z}/${coords.x}/${coords.y}.png`;
				const img = new Image();
				img.crossOrigin = 'anonymous';
				img.onload = () => {
					ctx.drawImage(img, 0, 0, size.x, size.y);
					done(undefined, canvas);
				};
				img.onerror = () => {
					this._drawTransparent(ctx, size);
					done(undefined, canvas);
				};
				img.src = osmUrl;
			} else {
				this._drawTransparent(ctx, size);
				done(undefined, canvas);
			}
			return;
		}

		// ===== Globaler OSM-Store (z7-12, Legacy) =====

		if (this._globalStore && coords.z >= 7 && coords.z <= this._globalMaxZoom!) {
			const key = this._tileKey(coords);
			const cached = this._getFromCache(key);
			if (cached) {
				this._drawImage(cached, ctx, size, done, canvas);
				return;
			}
			this.store.getProjectValue(key, this._globalStore).then((dataUrl: any) => {
				if (dataUrl) {
					this._addToCache(key, dataUrl);
					this._drawImage(dataUrl, ctx, size, done, canvas);
				} else if (this._onlineUrl && navigator.onLine) {
					this._loadOnlineTile(coords, ctx, size, done, canvas);
				} else {
					this._drawTransparent(ctx, size);
					done(undefined, canvas);
				}
			});
			return;
		}

		// Zoom 13+: Online-Tiles laden (via <img> statt fetch, um CORS-Fehler zu vermeiden)
		if (this._onlineUrl && navigator.onLine) {
			const key = this._tileKey(coords);
			const cached = this._getFromCache(key);
			if (cached) {
				this._drawImage(cached, ctx, size, done, canvas);
				return;
			}
			this._loadOnlineTile(coords, ctx, size, done, canvas);
			return;
		}

		// Zoom 13+ Offline: Memory-Cache prüfen
		const key = this._tileKey(coords);
		const cached = this._getFromCache(key);
		if (cached) {
			this._drawImage(cached, ctx, size, done, canvas);
			return;
		}

		// Zoom 13+ Offline: IndexedDB prüfen (nur wenn Projekt aktiv)
		if (this.options.project) {
			const storeName = this.options.name;
			const projectId = this.options.project;
			this.store.getProjectValue(key, projectId, storeName).then((dataUrl: any) => {
				if (dataUrl) {
					this._addToCache(key, dataUrl);
					this._drawImage(dataUrl, ctx, size, done, canvas);
				} else {
					// Kein heruntergeladenes Tile → Fallback auf niedrigere Zoomstufen oder Assets
					this._loadHighZoomFallback(coords, ctx, size, done, canvas);
				}
			});
		} else {
			// Kein Projekt → direkt Fallback
			this._loadHighZoomFallback(coords, ctx, size, done, canvas);
		}
	}

	/**
	 * Lädt ein hochskaliertes Tile aus dem globalen Store (z.B. z12 → z13+)
	 */
	private _loadUpscaledFromGlobalStore(
		coords: L.Coords,
		sourceZoom: number,
		ctx: CanvasRenderingContext2D,
		size: L.Point,
		done: L.DoneCallback,
		canvas: HTMLCanvasElement
	): void {
		const zoomDiff = coords.z - sourceZoom;
		const scale = Math.pow(2, zoomDiff);
		const srcX = Math.floor(coords.x / scale);
		const srcY = Math.floor(coords.y / scale);
		const subX = coords.x % scale;
		const subY = coords.y % scale;
		const srcKey = `${sourceZoom}_${srcX}_${srcY}`;

		// Memory-Cache prüfen
		const cached = this._getFromCache(srcKey);
		if (cached) {
			this._drawUpscaled(cached, scale, subX, subY, ctx, size, done, canvas);
			return;
		}

		// Globaler Store prüfen
		this.store.getProjectValue(srcKey, this._globalStore!).then((dataUrl: any) => {
			if (dataUrl) {
				this._addToCache(srcKey, dataUrl);
				this._drawUpscaled(dataUrl, scale, subX, subY, ctx, size, done, canvas);
			} else if (this._onlineUrl && navigator.onLine) {
				this._loadOnlineTile(coords, ctx, size, done, canvas);
			} else {
				this._drawTransparent(ctx, size);
				done(undefined, canvas);
			}
		});
	}

	/**
	 * Fallback für hohe Zoomstufen (13+):
	 * 1. Versuche niedrigere Zoomlevel aus IndexedDB hochzuskalieren
	 * 2. Dann Online-Tiles (nur wenn Netzwerk verfügbar)
	 * 3. Dann hochskalierte Tiles aus globalem Store (z.B. z12)
	 */
	/** Lädt z7-12 Tile aus Assets (/assets/osm/), Fallback auf Online */
	private _loadFromAssetOrOnline(
		coords: L.Coords,
		key: string,
		ctx: CanvasRenderingContext2D,
		size: L.Point,
		done: L.DoneCallback,
		canvas: HTMLCanvasElement
	): void {
		const assetUrl = `/assets/osm/${coords.z}/${coords.x}/${coords.y}.png`;
		const img = new Image();
		img.onload = () => {
			this._addToCache(key, assetUrl);
			ctx.drawImage(img, 0, 0, size.x, size.y);
			done(undefined, canvas);
		};
		img.onerror = () => {
			// Asset nicht vorhanden → Online-Fallback
			if (this._onlineUrl && navigator.onLine) {
				this._loadOnlineTile(coords, ctx, size, done, canvas);
			} else {
				this._drawTransparent(ctx, size);
				done(undefined, canvas);
			}
		};
		img.src = assetUrl;
	}

	/** Hochskaliertes Tile aus MBTiles rendern (z.B. z11 → z12+) */
	private _upscaleFromMBTiles(
		coords: L.Coords,
		sourceZoom: number,
		ctx: CanvasRenderingContext2D,
		size: L.Point,
		done: L.DoneCallback,
		canvas: HTMLCanvasElement
	): void {
		const zoomDiff = coords.z - sourceZoom;
		const scale = Math.pow(2, zoomDiff);
		const srcX = Math.floor(coords.x / scale);
		const srcY = Math.floor(coords.y / scale);
		const subX = coords.x % scale;
		const subY = coords.y % scale;

		const dataUrl = this._mbtiles!.getTile(sourceZoom, srcX, srcY);
		if (dataUrl) {
			this._drawUpscaled(dataUrl, scale, subX, subY, ctx, size, done, canvas);
		} else {
			this._drawTransparent(ctx, size);
			done(undefined, canvas);
		}
	}

	private _loadHighZoomFallback(
		coords: L.Coords,
		ctx: CanvasRenderingContext2D,
		size: L.Point,
		done: L.DoneCallback,
		canvas: HTMLCanvasElement
	): void {
		const minZoomForIndexedDB = (this._globalMaxZoom ?? this._mbtilesMaxZoom ?? 12) + 1;

		if (this.options.project && coords.z > minZoomForIndexedDB) {
			this._tryUpscaleFromLowerZoom(coords, coords.z - 1, ctx, size, done, canvas, minZoomForIndexedDB);
		} else if (this._onlineUrl && navigator.onLine) {
			this._loadOnlineTile(coords, ctx, size, done, canvas);
		} else if (this._globalStore && this._globalMaxZoom) {
			this._loadUpscaledFromGlobalStore(coords, this._globalMaxZoom, ctx, size, done, canvas);
		} else if (this._mbtiles?.ready() && this._mbtilesMaxZoom) {
			this._upscaleFromMBTiles(coords, this._mbtilesMaxZoom, ctx, size, done, canvas);
		} else {
			this._drawTransparent(ctx, size);
			done(undefined, canvas);
		}
	}

	/**
	 * Versucht rekursiv ein Tile von niedrigeren Zoomstufen zu laden und hochzuskalieren.
	 * Sucht von targetZoom abwärts bis minZoom.
	 */
	private _tryUpscaleFromLowerZoom(
		coords: L.Coords,
		targetZoom: number,
		ctx: CanvasRenderingContext2D,
		size: L.Point,
		done: L.DoneCallback,
		canvas: HTMLCanvasElement,
		minZoom = 7
	): void {
		// Abbruch wenn unter Minimalzoom — Online, globaler Store oder MBTiles Fallback
		if (targetZoom < minZoom) {
			if (this._onlineUrl && navigator.onLine) {
				this._loadOnlineTile(coords, ctx, size, done, canvas);
			} else if (this._globalStore && this._globalMaxZoom) {
				this._loadUpscaledFromGlobalStore(coords, this._globalMaxZoom, ctx, size, done, canvas);
			} else if (this._mbtiles?.ready() && this._mbtilesMaxZoom) {
				this._upscaleFromMBTiles(coords, this._mbtilesMaxZoom, ctx, size, done, canvas);
			} else {
				this._drawTransparent(ctx, size);
				done(undefined, canvas);
			}
			return;
		}

		const zoomDiff = coords.z - targetZoom;
		const scale = Math.pow(2, zoomDiff);
		const srcX = Math.floor(coords.x / scale);
		const srcY = Math.floor(coords.y / scale);
		const srcKey = `${targetZoom}_${srcX}_${srcY}`;

		// Erst Memory-Cache prüfen
		const cached = this._getFromCache(srcKey);
		if (cached) {
			const subX = coords.x % scale;
			const subY = coords.y % scale;
			this._drawUpscaled(cached, scale, subX, subY, ctx, size, done, canvas);
			return;
		}

		// IndexedDB prüfen
		this.store.getProjectValue(srcKey, this.options.project, this.options.name).then((dataUrl: any) => {
			if (dataUrl) {
				this._addToCache(srcKey, dataUrl);
				const subX = coords.x % scale;
				const subY = coords.y % scale;
				this._drawUpscaled(dataUrl, scale, subX, subY, ctx, size, done, canvas);
			} else {
				// Rekursiv nächst-niedrigere Zoomstufe versuchen
				this._tryUpscaleFromLowerZoom(coords, targetZoom - 1, ctx, size, done, canvas, minZoom);
			}
		});
	}

	/**
	 * Lädt ein Tile von der Online-URL (ohne Cache)
	 */
	private _loadOnlineTile(
		coords: L.Coords,
		ctx: CanvasRenderingContext2D,
		size: L.Point,
		done: L.DoneCallback,
		canvas: HTMLCanvasElement
	): void {
		const url = this._buildOnlineUrl(coords);
		this._drawImage(url, ctx, size, done, canvas, true);
	}

	/**
	 * Lädt ein Tile von der Online-URL und speichert es im Memory-Cache.
	 * Verwendet fetch mit AbortSignal für abbrechbare Requests.
	 */
	private _loadOnlineTileWithCache(
		coords: L.Coords,
		key: string,
		ctx: CanvasRenderingContext2D,
		size: L.Point,
		done: L.DoneCallback,
		canvas: HTMLCanvasElement,
		signal?: AbortSignal
	): void {
		const url = this._buildOnlineUrl(coords);

		// fetch mit AbortSignal für abbrechbare Requests
		const fetchOptions: RequestInit = { signal };
		if (this._authHeader) {
			fetchOptions.headers = { 'Authorization': this._authHeader };
		}
		fetch(url, fetchOptions)
			.then(response => {
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				return response.blob();
			})
			.then(blob => createImageBitmap(blob))
			.then(bitmap => {
				// Prüfen ob Request abgebrochen wurde
				if (signal?.aborted) return;

				ctx.drawImage(bitmap, 0, 0, size.x, size.y);
				bitmap.close();

				// Tile als DataURL im Cache speichern (ORIGINALE Farben, vor Transformation)
				try {
					const dataUrl = canvas.toDataURL('image/png');
					this._addToCache(key, dataUrl);
				} catch (e) {
					// Ignorieren
				}
				// Pixel-Transformation NACH dem Cache-Speichern (nur für Anzeige)
				this._applyPixelTransform(ctx, size);
				done(undefined, canvas);
			})
			.catch(err => {
				// Bei Abbruch nichts tun (done wurde nicht aufgerufen)
				if (err.name === 'AbortError') return;

				// CORS-Fallback: Tile über <img> laden (kein Cache möglich, aber anzeigbar)
				this._loadOnlineTileNoCors(url, ctx, size, done, canvas);
			});
	}

	/**
	 * Lädt ein Tile ohne CORS (Fallback wenn Server kein CORS unterstützt).
	 * Tile wird angezeigt, kann aber nicht gecached werden.
	 */
	private _loadOnlineTileNoCors(
		url: string,
		ctx: CanvasRenderingContext2D,
		size: L.Point,
		done: L.DoneCallback,
		canvas: HTMLCanvasElement
	): void {
		const img = new Image();
		img.onload = () => {
			ctx.drawImage(img, 0, 0, size.x, size.y);
			done(undefined, canvas);
		};
		img.onerror = () => {
			this._drawTransparent(ctx, size);
			done(undefined, canvas);
		};
		img.src = url;
	}

	/**
	 * Baut die Online-URL für ein Tile (XYZ oder WMS)
	 */
	private _buildOnlineUrl(coords: L.Coords): string {
		// WMS-URL bauen wenn wmsUrl gesetzt
		if (this._wmsUrl) {
			return this._buildWmsUrl(coords);
		}
		// Niedrige Zoomstufen: alternatives URL-Template (z.B. offizielles OSM)
		if (this._lowZoomUrl && this._lowZoomMaxZoom && coords.z <= this._lowZoomMaxZoom) {
			const s = this._onlineSubdomains![(coords.x + coords.y) % this._onlineSubdomains!.length];
			return this._lowZoomUrl
				.replace('{s}', s)
				.replace('{z}', String(coords.z))
				.replace('{x}', String(coords.x))
				.replace('{y}', String(coords.y));
		}
		// Standard XYZ-URL
		if (!this._onlineUrl) return '';
		const s = this._onlineSubdomains![(coords.x + coords.y) % this._onlineSubdomains!.length];
		return this._onlineUrl
			.replace('{s}', s)
			.replace('{z}', String(coords.z))
			.replace('{x}', String(coords.x))
			.replace('{y}', String(coords.y));
	}

	/**
	 * Baut eine WMS GetMap URL für die gegebenen Tile-Koordinaten
	 */
	private _buildWmsUrl(coords: L.Coords): string {
		if (!this._wmsUrl || !this._wmsLayers) return '';

		// Tile-Bounds in Lat/Lng berechnen (EPSG:4326)
		const size = this.getTileSize();
		const tileSize = size.x; // Quadratisch angenommen
		const nwPoint = coords.scaleBy(size);
		const sePoint = nwPoint.add(size);

		// Pixel zu LatLng mit Leaflet CRS
		const crs = L.CRS.EPSG3857;
		const nw = crs.pointToLatLng(nwPoint, coords.z);
		const se = crs.pointToLatLng(sePoint, coords.z);

		let bbox: string;
		let srs = this._wmsCrs;

		// CRS-spezifische BBOX-Berechnung
		if (this._wmsCrs === 'EPSG:3857') {
			// Web Mercator: Leaflet-Punkte zu Mercator-Metern konvertieren
			const nwMerc = crs.project(nw);
			const seMerc = crs.project(se);
			// BBOX: minX,minY,maxX,maxY
			bbox = `${nwMerc.x},${seMerc.y},${seMerc.x},${nwMerc.y}`;
		} else if (this._wmsCrs && this._wmsCrs !== 'EPSG:4326') {
			// Anderes CRS: Transformiere von EPSG:4326 (lng,lat) nach Ziel-CRS
			const nwTransformed = proj4('EPSG:4326', this._wmsCrs, [nw.lng, nw.lat]);
			const seTransformed = proj4('EPSG:4326', this._wmsCrs, [se.lng, se.lat]);
			// BBOX: minX,minY,maxX,maxY
			bbox = `${Math.min(nwTransformed[0], seTransformed[0])},${Math.min(nwTransformed[1], seTransformed[1])},${Math.max(nwTransformed[0], seTransformed[0])},${Math.max(nwTransformed[1], seTransformed[1])}`;
		} else {
			// EPSG:4326: BBOX im WMS 1.1.1 Format (minLng,minLat,maxLng,maxLat)
			bbox = `${nw.lng},${se.lat},${se.lng},${nw.lat}`;
			srs = 'EPSG:4326';
		}

		// URL-Parameter manuell zusammenbauen (ohne URLSearchParams, da
		// manche WMS-Server URL-encoded Sonderzeichen nicht korrekt verarbeiten,
		// z.B. EPSG%3A3857 statt EPSG:3857 oder image%2Fpng statt image/png)
		const params = [
			'service=WMS',
			'request=GetMap',
			'version=1.1.1',
			`layers=${this._wmsLayers}`,
			'styles=',
			`format=${this._wmsFormat}`,
			`transparent=${this._wmsTransparent ? 'true' : 'false'}`,
			`width=${tileSize}`,
			`height=${tileSize}`,
			`srs=${srs}`,
			`bbox=${bbox}`
		].join('&');

		// URL zusammenbauen
		const separator = this._wmsUrl.includes('?') ? '&' : '?';
		return `${this._wmsUrl}${separator}${params}`;
	}

	/**
	 * Lädt ein hochskaliertes Tile von einer niedrigeren Zoomstufe
	 */
	private _loadUpscaledTile(
		coords: L.Coords,
		sourceZoom: number,
		ctx: CanvasRenderingContext2D,
		size: L.Point,
		done: L.DoneCallback,
		canvas: HTMLCanvasElement,
		signal?: AbortSignal
	): void {
		const zoomDiff = coords.z - sourceZoom;
		const scale = Math.pow(2, zoomDiff);

		const srcX = Math.floor(coords.x / scale);
		const srcY = Math.floor(coords.y / scale);
		const srcKey = `${sourceZoom}_${srcX}_${srcY}`;

		const subX = coords.x % scale;
		const subY = coords.y % scale;

		// Erst Memory-Cache prüfen
		const cached = this._getFromCache(srcKey);
		if (cached) {
			this._drawUpscaled(cached, scale, subX, subY, ctx, size, done, canvas);
			return;
		}

		this.store.getProjectValue(srcKey, this.options.project, this.options.name).then((dataUrl: any) => {
			if (dataUrl) {
				this._addToCache(srcKey, dataUrl);
				this._drawUpscaled(dataUrl, scale, subX, subY, ctx, size, done, canvas);
			} else if (this._wmsUrl && navigator.onLine) {
				// WMS-Tile bei sourceZoom laden, cachen, dann hochskaliert zeichnen
				const srcCoords = { x: srcX, y: srcY, z: sourceZoom } as L.Coords;
				// Temporäres Canvas für das volle Source-Tile erstellen
				const tmpCanvas = document.createElement('canvas');
				tmpCanvas.width = size.x;
				tmpCanvas.height = size.y;
				const tmpCtx = tmpCanvas.getContext('2d')!;
				this._loadOnlineTileWithCache(srcCoords, srcKey, tmpCtx, size, (err, tile) => {
					try {
						const srcDataUrl = tmpCanvas.toDataURL('image/png');
						this._addToCache(srcKey, srcDataUrl);
						this._drawUpscaled(srcDataUrl, scale, subX, subY, ctx, size, done, canvas);
					} catch (e) {
						// toDataURL kann bei tainted Canvas fehlschlagen
						this._drawTransparent(ctx, size);
						done(undefined, canvas);
					}
				}, tmpCanvas, signal);
			} else if (this._onlineUrl && navigator.onLine) {
				// Online-Tiles für höhere Zoomstufen direkt laden
				this._loadOnlineTile(coords, ctx, size, done, canvas);
			} else if (this._globalStore && this._globalMaxZoom) {
				// Fallback auf globalen Store — hochskalieren von globalMaxZoom
				this._loadUpscaledFromGlobalStore(coords, this._globalMaxZoom, ctx, size, done, canvas);
			} else {
				this._drawTransparent(ctx, size);
				done(undefined, canvas);
			}
		});
	}

	/** Wendet die optionale Pixel-Transformation auf den Canvas an (z.B. DGM-Farbkalibrierung) */
	private _applyPixelTransform(ctx: CanvasRenderingContext2D, size: L.Point): void {
		if (!this.options.pixelTransform) return;
		try {
			const imageData = ctx.getImageData(0, 0, size.x, size.y);
			this.options.pixelTransform(imageData);
			ctx.putImageData(imageData, 0, 0);
		} catch (e) {
			// CORS-Fehler bei getImageData — Tile wird mit Originalfarben angezeigt
		}
	}

	/**
	 * Zeichnet ein Bild direkt auf das Canvas
	 */
	private _drawImage(
		src: string,
		ctx: CanvasRenderingContext2D,
		size: L.Point,
		done: L.DoneCallback,
		canvas: HTMLCanvasElement,
		isAsset = false
	): void {
		const img = new Image();
		img.onload = () => {
			ctx.drawImage(img, 0, 0, size.x, size.y);
			this._applyPixelTransform(ctx, size);
			done(undefined, canvas);
		};
		img.onerror = () => {
			this._drawTransparent(ctx, size);
			done(undefined, canvas);
		};
		img.src = src;
	}

	/**
	 * Zeichnet einen hochskalierten Ausschnitt eines Tiles
	 */
	private _drawUpscaled(
		src: string,
		scale: number,
		subX: number,
		subY: number,
		ctx: CanvasRenderingContext2D,
		size: L.Point,
		done: L.DoneCallback,
		canvas: HTMLCanvasElement,
		isAsset = false
	): void {
		const img = new Image();
		img.onload = () => {
			const srcTileSize = img.width;
			const srcSubSize = srcTileSize / scale;
			const sx = subX * srcSubSize;
			const sy = subY * srcSubSize;

			ctx.imageSmoothingEnabled = false;
			ctx.drawImage(
				img,
				sx, sy, srcSubSize, srcSubSize,
				0, 0, size.x, size.y
			);
			this._applyPixelTransform(ctx, size);
			done(undefined, canvas);
		};
		img.onerror = () => {
			this._drawTransparent(ctx, size);
			done(undefined, canvas);
		};
		img.src = src;
	}

	private _drawTransparent(ctx: CanvasRenderingContext2D, size: L.Point): void {
		ctx.fillStyle = 'transparent';
		ctx.fillRect(0, 0, size.x, size.y);
	}

	private _tileKey(coords: L.Coords): string {
		return `${coords.z}_${coords.x}_${coords.y}`;
	}
}
